// Utilities for submission cycles and status, using PST time.

const PST_TIME_ZONE = 'America/Los_Angeles';

// Reference cycle: 2026-03-02 00:00 to 2026-03-12 23:59 PST, then every 28 days.
const REF_START_PST = '2026-03-02T00:00:00-08:00';
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const CYCLE_DAYS = 28;

/**
 * Get a Date representing the current time in PST.
 */
export function getNowInPst() {
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: PST_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  const parts = formatter.formatToParts(now).reduce((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});

  const year = Number(parts.year);
  const month = Number(parts.month);
  const day = Number(parts.day);
  const hour = Number(parts.hour);
  const minute = Number(parts.minute);
  const second = Number(parts.second);

  return new Date(Date.UTC(year, month - 1, day, hour, minute, second));
}

// Window length: 10 days (Mar 2 00:00 to Mar 12 23:59:59 PST)
const WINDOW_END_OFFSET_DAYS = 10;
const WINDOW_END_OFFSET_MS =
  WINDOW_END_OFFSET_DAYS * MS_PER_DAY + (23 * 3600 + 59 * 60 + 59) * 1000;

/**
 * Return the current submission cycle window and allowed fill window (PST semantics).
 * Uses Date.now() for cycle index so boundaries are correct. nowPst is optional (for API compatibility).
 */
export function getSubmissionCycleFor(nowPst) {
  const refStart = new Date(REF_START_PST);
  const refStartMs = refStart.getTime();
  const nowMs = Date.now();
  const daysSinceRefStart = (nowMs - refStartMs) / MS_PER_DAY;
  const k = daysSinceRefStart >= 0 ? Math.floor(daysSinceRefStart / CYCLE_DAYS) : -1;

  const cycleStart = new Date(refStartMs + k * CYCLE_DAYS * MS_PER_DAY);
  const cycleEndExclusive = new Date(refStartMs + (k + 1) * CYCLE_DAYS * MS_PER_DAY);
  const windowStart = new Date(cycleStart.getTime());
  const windowEnd = new Date(cycleStart.getTime() + WINDOW_END_OFFSET_MS);

  return { start: cycleStart, endExclusive: cycleEndExclusive, windowStart, windowEnd };
}

/**
 * Parse submission time from API (naive datetime, stored as PST) to a Date.
 * If the string has no timezone (no Z or ±HH:MM), treat it as PST (-08:00).
 */
function parseSubmissionTimeAsPst(iso) {
  if (!iso || typeof iso !== 'string') return null;
  const s = iso.trim();
  if (s.endsWith('Z') || /[+-]\d{2}:?\d{2}$/.test(s)) return new Date(s);
  const withPst = s.includes('T') ? s + '-08:00' : s + 'T00:00:00-08:00';
  return new Date(withPst);
}

/**
 * Compute submission status for the given submissionTime ISO string.
 * Returns 'YES' | 'NO' | 'EXPIRED'.
 * submissionTime is stored in DB as PST (naive); we parse it as PST for comparison.
 */
export function getSubmissionStatus(submissionTimeIso, nowPst) {
  const nowMs = Date.now();
  const { start, endExclusive, windowStart, windowEnd } = getSubmissionCycleFor(nowMs);
  let submittedInWindow = false;
  let submittedAfterWindowInCycle = false;

  if (submissionTimeIso) {
    const sub = parseSubmissionTimeAsPst(submissionTimeIso);
    if (sub) {
      submittedInWindow = sub.getTime() >= windowStart.getTime() && sub.getTime() <= windowEnd.getTime();
      const inCycle = sub.getTime() >= start.getTime() && sub.getTime() < endExclusive.getTime();
      submittedAfterWindowInCycle = inCycle && sub.getTime() > windowEnd.getTime();
    }
  }

  if (submittedAfterWindowInCycle) return 'EXPIRED';
  if (submittedInWindow) return 'YES';

  if (nowMs < start.getTime()) return 'NO';
  if (nowMs < endExclusive.getTime()) {
    if (nowMs > windowEnd.getTime()) return 'EXPIRED';
    return 'NO';
  }
  return 'EXPIRED';
}

export function isSubmittedInCurrentCycle(status) {
  return status === 'YES';
}

